import { useState, type FormEvent } from 'react';
import { Pause, Play, Plus, Trash2 } from 'lucide-react';
import type { SubscriptionState, TargetDTO } from '@bb/shared';
import { ApiError } from '@/lib/api';
import { count, relative, utcStamp } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Button, IconButton } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { StatusDot, type DotTone } from '@/components/ui/StatusDot';
import { EmptyState, ErrorState, Sweep } from '@/components/ui/States';
import { useTargetActions, useTargets } from './useTargets';

const SUBSCRIPTION_TONE: Record<SubscriptionState, DotTone> = {
  ACTIVE: 'ok',
  PENDING: 'warn',
  ERROR: 'alert',
  NONE: 'idle',
};

function AddTargetForm() {
  const { create } = useTargetActions();
  const [handle, setHandle] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!handle.trim()) return;
    create.mutate(
      { handle },
      {
        onSuccess: () => setHandle(''),
      },
    );
  };

  const message =
    create.error instanceof ApiError
      ? create.error.message
      : create.error
        ? 'Could not add the target.'
        : null;

  return (
    <form onSubmit={submit} className="flex flex-col gap-2">
      <div className="flex gap-2">
        <Input
          value={handle}
          onChange={(event) => setHandle(event.target.value)}
          placeholder="@handle or x.com/handle"
          aria-label="X handle"
          className="flex-1"
        />
        <Button
          type="submit"
          variant="primary"
          disabled={create.isPending || !handle.trim()}
          icon={<Plus size={13} strokeWidth={2} />}
        >
          Watch
        </Button>
      </div>
      {create.isPending ? <Sweep /> : null}
      {message ? (
        <p className="border border-alert/40 bg-alert-dim px-2.5 py-1.5 font-mono text-[11px] text-alert">
          {message}
        </p>
      ) : null}
    </form>
  );
}

function TargetRow({ target }: { target: TargetDTO }) {
  const { update, remove } = useTargetActions();
  const [confirming, setConfirming] = useState(false);

  const initials = target.displayName.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();

  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-panel-alt/60">
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          {target.avatarUrl ? (
            <img
              src={target.avatarUrl}
              alt=""
              className="size-7 rounded-sharp border border-line object-cover"
            />
          ) : (
            <div
              className="flex size-7 items-center justify-center rounded-sharp border bg-panel-alt font-mono text-[10px]"
              style={{
                borderColor: target.color ?? 'var(--color-line-bright)',
                color: target.color ?? 'var(--color-ink-dim)',
              }}
            >
              {initials || '??'}
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate text-[13px] text-ink">{target.displayName}</div>
            <div className="data-mono truncate text-[11px] text-ink-dim">@{target.handle}</div>
          </div>
        </div>
      </td>

      <td className="px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          <StatusDot
            tone={target.isActive ? SUBSCRIPTION_TONE[target.subscriptionState] : 'idle'}
            pulse={target.isActive && target.subscriptionState === 'PENDING'}
          />
          <span className="data-mono text-[11px] uppercase text-ink-dim">
            {target.isActive ? target.subscriptionState : 'Paused'}
          </span>
        </div>
        {target.subscriptionError ? (
          <div className="mt-1 font-mono text-[10px] text-alert">{target.subscriptionError}</div>
        ) : null}
      </td>

      <td className="data-mono px-3 py-2.5 text-right text-[11px] text-ink-dim">
        {count(target.followersCount)}
      </td>

      <td className="data-mono px-3 py-2.5 text-right text-[11px] text-ink">
        {count(target.postCount)}
      </td>

      <td
        className="data-mono px-3 py-2.5 text-right text-[11px] text-ink-dim"
        title={target.lastPostAt ? utcStamp(target.lastPostAt) : undefined}
      >
        {target.lastPostAt ? relative(target.lastPostAt) : '—'}
      </td>

      <td className="px-3 py-2.5">
        <div className="flex items-center justify-end gap-1">
          <IconButton
            onClick={() => update.mutate({ id: target.id, patch: { isActive: !target.isActive } })}
            disabled={update.isPending}
            title={target.isActive ? 'Pause — keeps the archive' : 'Resume'}
            aria-label={target.isActive ? 'Pause target' : 'Resume target'}
          >
            {target.isActive ? (
              <Pause size={13} strokeWidth={1.75} />
            ) : (
              <Play size={13} strokeWidth={1.75} />
            )}
          </IconButton>

          {confirming ? (
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="danger"
                onClick={() => remove.mutate({ id: target.id, confirm: true })}
                disabled={remove.isPending}
              >
                Delete {count(target.postCount)} posts
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <IconButton
              onClick={() => setConfirming(true)}
              title="Remove target and its archived posts"
              aria-label="Remove target"
              className="hover:text-alert"
            >
              <Trash2 size={13} strokeWidth={1.75} />
            </IconButton>
          )}
        </div>
      </td>
    </tr>
  );
}

export function TargetsPage() {
  const targets = useTargets();

  return (
    <div className="grid h-full grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
      <div className="flex flex-col gap-4">
        <Panel label="Add target">
          <AddTargetForm />
          <p className="mt-3 font-mono text-[10px] leading-relaxed text-ink-faint">
            Each target becomes a subscription to that account&rsquo;s posts. Pausing stops delivery
            but keeps everything already archived.
          </p>
        </Panel>
      </div>

      <Panel label="Watch list" flush className="min-h-0" bodyClassName="overflow-auto">
        {targets.isPending ? (
          <div className="p-4">
            <Sweep />
          </div>
        ) : targets.error ? (
          <ErrorState
            message={targets.error instanceof Error ? targets.error.message : 'Load failed.'}
            action={
              <Button size="sm" onClick={() => void targets.refetch()}>
                Retry
              </Button>
            }
          />
        ) : (targets.data?.length ?? 0) === 0 ? (
          <EmptyState
            title="Watch list empty"
            detail="Add an X account on the left. Its posts will start arriving in the feed."
          />
        ) : (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-panel-alt">
              <tr className="border-b border-line">
                <th className="label-xs px-3 py-2 text-left">Account</th>
                <th className="label-xs px-3 py-2 text-left">Subscription</th>
                <th className="label-xs px-3 py-2 text-right">Followers</th>
                <th className="label-xs px-3 py-2 text-right">Posts</th>
                <th className="label-xs px-3 py-2 text-right">Last</th>
                <th className="label-xs px-3 py-2 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {targets.data?.map((target) => <TargetRow key={target.id} target={target} />)}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
